import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLenders,
  getLendersList,
  getLender,
  createLender,
  updateLender,
  updateLenderProductConfiguration,
  getLenderProducts,
  removeLenderProduct,
  getLenderUsers,
  getLenderLogo,
  uploadLenderLogo,
  blockLender,
  unblockLender,
  deleteLender,
  addLenderUser,
  getLenderUser,
  updateLenderUser,
  getLenderUserCascades,
  importLenderUsers,
  type AddLenderUserRequest,
  type UpdateLenderUserRequest,
  type CreateLenderRequest,
  type ImportedLenderUser,
  type LendersListParams,
  type UpdateLenderRequest,
  type UpdateProductConfigurationRequest,
} from '../api/lenders';

export function useLenders() {
  return useQuery({
    queryKey: ['lenders'],
    queryFn: getLenders,
  });
}

export function useLendersList(params: LendersListParams) {
  return useQuery({
    // Under 'lenders', so the create, block and unblock mutations refresh the table too.
    queryKey: ['lenders', 'list', params],
    queryFn: () => getLendersList(params),
    placeholderData: keepPreviousData,
  });
}

export function useLender(urn: string) {
  return useQuery({
    queryKey: ['lenders', urn],
    queryFn: () => getLender(urn),
    enabled: !!urn,
  });
}

export function useLenderProducts(urn: string) {
  return useQuery({
    queryKey: ['lenders', urn, 'products'],
    queryFn: () => getLenderProducts(urn),
    enabled: !!urn,
  });
}

export function useLenderUsers(urn: string) {
  return useQuery({
    queryKey: ['lenders', urn, 'users'],
    queryFn: () => getLenderUsers(urn),
    enabled: !!urn,
  });
}

export function useAddLenderUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, data }: { urn: string; data: AddLenderUserRequest }) => addLenderUser(urn, data),
    // Under 'lenders': the lender's users tab and the user count in the lenders table.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useUpdateLenderUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, userUrn, data }: { urn: string; userUrn: string; data: UpdateLenderUserRequest }) =>
      updateLenderUser(urn, userUrn, data),
    // Same refresh as Add User: the lender's users tab and the lenders table count.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

/**
 * One lender user with its product grants resolved — only fetched while the editor is open. Under
 * 'lenders', so saving the user refreshes it along with the users tab.
 */
export function useLenderUser(urn: string | undefined, userUrn: string | undefined) {
  return useQuery({
    queryKey: ['lenders', urn, 'users', userUrn, 'detail'],
    queryFn: () => getLenderUser(urn as string, userUrn as string),
    enabled: Boolean(urn && userUrn),
  });
}

/** The lender's cascades with this user's membership — only fetched while the editor is open. */
export function useLenderUserCascades(urn: string | undefined, userUrn: string | undefined) {
  return useQuery({
    queryKey: ['lenders', urn, 'users', userUrn, 'cascades'],
    queryFn: () => getLenderUserCascades(urn as string, userUrn as string),
    enabled: Boolean(urn && userUrn),
  });
}

export function useImportLenderUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, users }: { urn: string; users: ImportedLenderUser[] }) => importLenderUsers(urn, users),
    // Same refresh as Add User: the users tab and the lenders table count.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

/** The lender's profile picture as a Blob; null when it has none. */
export function useLenderLogo(urn: string) {
  return useQuery({
    queryKey: ['lenders', urn, 'logo'],
    queryFn: () => getLenderLogo(urn),
    enabled: !!urn,
  });
}

export function useUploadLenderLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, file }: { urn: string; file: File }) => uploadLenderLogo(urn, file),
    onSuccess: (_result, { urn }) => {
      qc.invalidateQueries({ queryKey: ['lenders', urn, 'logo'] });
    },
  });
}

export function useCreateLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLenderRequest) => createLender(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useUpdateLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, data }: { urn: string; data: UpdateLenderRequest }) =>
      updateLender(urn, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useUpdateLenderProductConfiguration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, data }: { urn: string; data: UpdateProductConfigurationRequest }) =>
      updateLenderProductConfiguration(urn, data),
    onSuccess: (_result, { urn }) => {
      qc.invalidateQueries({ queryKey: ['lenders', urn] });
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useBlockLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (urn: string) => blockLender(urn),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useUnblockLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (urn: string) => unblockLender(urn),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useDeleteLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, confirmUrn }: { urn: string; confirmUrn: string }) =>
      deleteLender(urn, confirmUrn),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useRemoveLenderProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, productUrn }: { urn: string; productUrn: string }) =>
      removeLenderProduct(urn, productUrn),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}
